import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Spinner, Alert, Form, Button, Pagination } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface Transaction {
  _id: string;
  type: string;
  amount: number;
  description: string;
  status: string;
  sourceId?: string;
  reason?: string;
  createdAt: string;
  metadata?: {
    adminName?: string;
    adminEmail?: string;
    reason?: string;
    balanceBefore?: number;
    balanceAfter?: number;
  };
  adminId?: {
    name: string;
    email: string;
  };
}

interface PaginationInfo {
  current: number;
  total: number;
  count: number;
  totalRecords: number;
}

const TransactionHistory: React.FC = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState<PaginationInfo>({
    current: 1,
    total: 1,
    count: 0,
    totalRecords: 0
  });
  
  // Filter states
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, typeFilter, statusFilter]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });

      if (typeFilter) params.append('type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API_BASE_URL}/users/rewards/transactions?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setTransactions(response.data.transactions);
        setPagination(response.data.pagination);
      } else {
        setError(response.data.message || 'Failed to fetch transactions');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeDisplay = (type: string) => {
    const typeMap: { [key: string]: { label: string; variant: string } } = {
      'task_reward': { label: 'Task Rewards', variant: 'success' },
      'referral_reward': { label: 'Referral Task Reward', variant: 'info' },
      'referral_signup_bonus': { label: 'Referral Signup Bonus', variant: 'primary' },
      'signup_bonus': { label: 'Signup Bonus', variant: 'primary' },
      'commission_earning': { label: 'Commission Earning', variant: 'success' },
      'manual_bonus': { label: 'Manual Bonus', variant: 'warning' },
      'withdrawal': { label: 'Withdrawal', variant: 'danger' },
      'upgrade_commission_l1': { label: 'Referral Bonus L1', variant: 'success' },
      'upgrade_commission_l2': { label: 'Referral Bonus L2', variant: 'info' },
      'upgrade_commission_l3': { label: 'Referral Bonus L3', variant: 'warning' },
      'upgrade_commission_l4': { label: 'Referral Bonus L4', variant: 'primary' }
    };

    return typeMap[type] || { label: type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), variant: 'dark' };
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatAmount = (amount: number, type: string) => {
    const isDebit = type === 'withdrawal';
    const prefix = isDebit ? '-' : '+';
    const colorClass = isDebit ? 'text-danger' : 'text-success';
    
    return (
      <span className={colorClass}>
        {prefix}₹{Math.abs(amount).toFixed(2)}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const resetFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const getTotalBalance = () => {
    return transactions.reduce((total, transaction) => {
      if (transaction.type === 'withdrawal') {
        return total - transaction.amount;
      }
      return total + transaction.amount;
    }, 0);
  };

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>Transaction History</h1>
              <p className="text-muted">View all your reward transactions and withdrawals</p>
            </div>
            <div className="text-end">
              <div className="mb-2">
                <small className="text-muted">Current Balance</small>
              </div>
              <h3 className="text-success mb-0">₹{user?.rewardBalance?.toFixed(2) || '0.00'}</h3>
            </div>
          </div>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Type</Form.Label>
                <Form.Select
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Types</option>
                  <option value="task_reward">Task Rewards</option>
                  <option value="referral_reward">Referral Task Rewards</option>
                  <option value="referral_signup_bonus">Referral Signup Bonus</option>
                  <option value="signup_bonus">Signup Bonus</option>
                  <option value="commission_earning">Commission Earnings</option>
                  <option value="manual_bonus">Manual Bonus</option>
                  <option value="withdrawal">Withdrawals</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>&nbsp;</Form.Label>
                <div>
                  <Button variant="outline-secondary" onClick={resetFilters}>
                    <i className="fas fa-refresh me-2"></i>
                    Reset Filters
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Transaction Summary */}
      {pagination.totalRecords > 0 && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-primary">{pagination.totalRecords}</h4>
                <p className="text-muted mb-0">Total Transactions</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-success">
                  {transactions.filter(t => t.type !== 'withdrawal').length}
                </h4>
                <p className="text-muted mb-0">Earnings</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-danger">
                  {transactions.filter(t => t.type === 'withdrawal').length}
                </h4>
                <p className="text-muted mb-0">Withdrawals</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-info">
                  {transactions.filter(t => t.status === 'pending').length}
                </h4>
                <p className="text-muted mb-0">Pending</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Transactions Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <div className="mt-2">Loading transactions...</div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-receipt fa-3x text-muted mb-3"></i>
              <h5>No Transactions Found</h5>
              <p className="text-muted">
                {typeFilter || statusFilter 
                  ? 'No transactions match your current filters.'
                  : 'You haven\'t made any transactions yet. Start completing tasks to earn rewards!'}
              </p>
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => {
                    const typeInfo = getTransactionTypeDisplay(transaction.type);
                    return (
                      <tr key={transaction._id}>
                        <td>
                          <div>
                            <div className="fw-bold">{formatDate(transaction.createdAt).split(',')[0]}</div>
                            <small className="text-muted">{formatDate(transaction.createdAt).split(',')[1]}</small>
                          </div>
                        </td>
                        <td>
                          <Badge bg={typeInfo.variant} className="px-2 py-1">
                            {typeInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <div>
                            <div>{transaction.description}</div>
                            {transaction.reason && (
                              <small className="text-muted">Reason: {transaction.reason}</small>
                            )}
                            {transaction.adminId && (
                              <small className="text-muted d-block">
                                By: {transaction.adminId.name}
                              </small>
                            )}
                          </div>
                        </td>
                        <td>
                          <strong>{formatAmount(transaction.amount, transaction.type)}</strong>
                        </td>
                        <td>
                          {getStatusBadge(transaction.status)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {/* Pagination */}
              {pagination.total > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <div className="text-muted">
                    Showing {((pagination.current - 1) * itemsPerPage) + 1} to {Math.min(pagination.current * itemsPerPage, pagination.totalRecords)} of {pagination.totalRecords} transactions
                  </div>
                  <Pagination>
                    <Pagination.Prev 
                      disabled={pagination.current === 1}
                      onClick={() => handlePageChange(pagination.current - 1)}
                    />
                    {Array.from({ length: pagination.total }, (_, i) => i + 1).map(page => (
                      <Pagination.Item
                        key={page}
                        active={page === pagination.current}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next 
                      disabled={pagination.current === pagination.total}
                      onClick={() => handlePageChange(pagination.current + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default TransactionHistory;